import { useState, useMemo, useEffect } from 'react';
import { useShelterStore } from '../../../store/shelterStore';
import { useAuthStore } from '../../../store/authStore';
import { notifications } from '../../../lib/notifications';
import type { Shelter, Survivor, TriageQuestion } from '../../../types/shelter';
import { IconCheck, IconPlus, IconSearch, IconId, IconFlag, IconClipboardCheck, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import DLScanner from '../../../components/scanner/DLScanner';
import type { DLData } from '../../../lib/dlParser';

interface Props {
  shelter: Shelter;
}

const empty = () => ({
  firstName: '', lastName: '', dateOfBirth: '', gender: '',
  phone: '', emergencyContactName: '', emergencyContactPhone: '',
  specialNeeds: '', dietaryNeeds: '', cotAssignment: '', notes: '',
});

// ── Mobile triage field renderer ──────────────────────────────

function MobileTriageField({
  q, qKey, value, onChange,
}: {
  q: TriageQuestion;
  qKey: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const isFlagged = q.flagIfAnswer && value === q.flagIfAnswer;

  return (
    <div style={{
      background: isFlagged ? '#fffbeb' : '#F8F8F8',
      border: `1px solid ${isFlagged ? '#fde68a' : '#e2e8f0'}`,
      borderRadius: 4, padding: '16px 16px',
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#1B1C1C', marginBottom: 8, lineHeight: 1.4 }}>
        {q.question}
        {isFlagged && (
          <span style={{ marginLeft: 8, color: '#d97706', fontSize: 12, fontWeight: 700 }}>
            <IconFlag size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />FLAGGED
          </span>
        )}
      </div>

      {q.questionType === 'yes_no' && (
        <div style={{ display: 'flex', gap: 8 }}>
          {['Yes', 'No'].map(opt => (
            <button key={opt} onClick={() => onChange(opt)} style={{
              flex: 1, padding: '9px 0', border: '1.5px solid',
              borderRadius: 4, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: value === opt ? '#4B3FFF' : 'white',
              color:      value === opt ? 'white'    : '#475569',
              borderColor: value === opt ? '#4B3FFF'  : '#cbd5e1',
            }}>
              {opt}
            </button>
          ))}
        </div>
      )}

      {q.questionType === 'multiple_choice' && (() => {
        let opts: string[] = [];
        try { opts = JSON.parse(q.options); } catch { opts = []; }
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {opts.map(opt => (
              <button key={opt} onClick={() => onChange(opt)} style={{
                padding: '9px 16px', textAlign: 'left', border: '1.5px solid', borderRadius: 4,
                fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
                background: value === opt ? '#eeeeff' : 'white',
                color:      value === opt ? '#2C01D6' : '#475569',
                borderColor: value === opt ? '#4B3FFF' : '#cbd5e1',
              }}>
                {opt}
              </button>
            ))}
          </div>
        );
      })()}

      {q.questionType === 'text' && (
        <textarea
          className="sp-textarea"
          style={{ minHeight: 64, marginBottom: 0 }}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="Enter response…"
        />
      )}

      {q.questionType === 'scale' && (
        <div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(n => (
              <button key={n} onClick={() => onChange(n)} style={{
                width: 40, height: 40, border: '1.5px solid', borderRadius: 4,
                fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                background: value === n ? '#4B3FFF' : 'white',
                color:      value === n ? 'white'   : '#475569',
                borderColor: value === n ? '#4B3FFF' : '#cbd5e1',
              }}>
                {n}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#939598', marginTop: 4 }}>
            <span>1 = Low</span><span>10 = High</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main intake form ──────────────────────────────────────────

// ── Pre-registered check-in section ──────────────────────────

function PreRegSection({ shelter }: { shelter: Shelter }) {
  const { user } = useAuthStore();
  const { survivors, updateSurvivor, loadShelters } = useShelterStore();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Survivor | null>(null);
  const [cot, setCot] = useState('');
  const [saving, setSaving] = useState(false);

  const preReg = useMemo(() =>
    survivors.filter(s => s.shelterId === String(shelter.id) && s.status === 'pre-registered'),
    [survivors, shelter.id],
  );

  const filtered = useMemo(() => {
    if (!search) return preReg;
    const q = search.toLowerCase();
    return preReg.filter(s =>
      `${s.firstName} ${s.lastName}`.toLowerCase().includes(q) ||
      (s.qrCode || '').toLowerCase().includes(q),
    );
  }, [preReg, search]);

  const handleCheckIn = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await updateSurvivor(selected.id, {
        status: 'checked_in',
        intakeTimestamp: new Date().toISOString(),
        intakeBy: (user as any)?.email ?? 'portal',
        cotAssignment: cot.trim() || selected.cotAssignment,
      } as any);
      await loadShelters();
      notifications.show({ title: 'Checked In', message: `${selected.firstName} ${selected.lastName} is now checked in.`, color: 'var(--color-success)' });
      setSelected(null);
      setCot('');
    } catch {
      notifications.show({ title: 'Error', message: 'Check-in failed. Please try again.', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  if (preReg.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#f0eeff', border: '1.5px solid #c4b5fd', borderRadius: 4,
          padding: '16px 16px', cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <IconClipboardCheck size={18} color="#4B3FFF" />
          <span style={{ fontSize: 14, fontWeight: 700, color: '#2C01D6' }}>Pre-Registered ({preReg.length})</span>
          <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 500 }}>awaiting check-in</span>
        </div>
        {open ? <IconChevronUp size={16} color="#4B3FFF" /> : <IconChevronDown size={16} color="#4B3FFF" />}
      </button>

      {open && (
        <div style={{ border: '1.5px solid #c4b5fd', borderTop: 'none', borderRadius: '0 0 10px 8px', background: 'white', padding: 16 }}>
          {/* Search */}
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <IconSearch size={14} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
            <input
              className="sp-input"
              style={{ paddingLeft: 32, fontSize: 14 }}
              placeholder="Search name or PRE- code…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.length === 0 && (
              <div style={{ textAlign: 'center', padding: '16px 0', color: '#939598', fontSize: 14 }}>No results</div>
            )}
            {filtered.map(s => (
              <div
                key={s.id}
                onClick={() => { setSelected(s); setCot(''); }}
                style={{
                  background: '#f8f7ff', border: '1.5px solid #ddd6fe', borderRadius: 4,
                  padding: '8px 16px', cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#1B1C1C' }}>{s.firstName} {s.lastName}</div>
                    {s.qrCode && <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginTop: 1 }}>{s.qrCode}</div>}
                    {s.specialNeeds && (
                      <div style={{ fontSize: 12, color: '#b45309', marginTop: 3 }}>
                        ⚠ {s.specialNeeds.replace(/[\[\]"]/g, '')}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 12, color: '#4B3FFF', fontWeight: 700, whiteSpace: 'nowrap', marginLeft: 8 }}>Check In →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Check-in drawer */}
      {selected && (
        <div className="sp-drawer-overlay" onClick={() => setSelected(null)}>
          <div className="sp-drawer" onClick={e => e.stopPropagation()}>
            <div className="sp-drawer-handle" />
            <div style={{ fontSize: 16, fontWeight: 800, color: '#1B1C1C', marginBottom: 4 }}>
              {selected.firstName} {selected.lastName}
            </div>
            {selected.qrCode && (
              <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginBottom: 16 }}>{selected.qrCode}</div>
            )}

            {selected.specialNeeds && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 14 }}>
                <div style={{ fontWeight: 700, color: '#991b1b', marginBottom: 2 }}>Special Needs</div>
                <div>{selected.specialNeeds.replace(/[\[\]"]/g, '')}</div>
              </div>
            )}

            {selected.dietaryNeeds && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '8px 16px', marginBottom: 16, fontSize: 14 }}>
                <div style={{ fontWeight: 700, color: '#92400e', marginBottom: 2 }}>Dietary Needs</div>
                <div>{selected.dietaryNeeds}</div>
              </div>
            )}

            {selected.phone && (
              <div style={{ fontSize: 14, color: '#475569', marginBottom: 16 }}>
                <span style={{ fontWeight: 600 }}>Phone: </span>{selected.phone}
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 8 }}>
                Cot Assignment (optional)
              </label>
              <input
                className="sp-input"
                value={cot}
                onChange={e => setCot(e.target.value)}
                placeholder="Wing B, Cot 14"
              />
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button className="sp-btn sp-btn-secondary sp-btn-sm" style={{ flex: 1 }} onClick={() => setSelected(null)}>
                Cancel
              </button>
              <button className="sp-btn sp-btn-primary sp-btn-sm" style={{ flex: 1 }} onClick={handleCheckIn} disabled={saving}>
                {saving ? 'Saving…' : <><IconCheck size={14} /> Complete Check-In</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main intake form ──────────────────────────────────────────

export default function ShelterPortalIntake({ shelter }: Props) {
  const { activeIncident, user } = useAuthStore();
  const {
    createSurvivor, createFamily, createTriageAnswer,
    families, loadShelters,
    triageQuestions, loadTriageQuestions,
  } = useShelterStore();

  const [form, setForm] = useState(empty());
  const [triageAnswers, setTriageAnswers] = useState<Record<string, string>>({});
  const [familyMode, setFamilyMode] = useState<'none' | 'new' | 'existing'>('none');
  const [newFamilyName, setNewFamilyName] = useState('');
  const [familySearch, setFamilySearch] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [doneFlags, setDoneFlags] = useState(0);
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => { loadTriageQuestions(); }, [loadTriageQuestions]);

  const activeQuestions: TriageQuestion[] = useMemo(
    () => triageQuestions.filter((q: any) => q.isActive).sort((a: any, b: any) => a.sortOrder - b.sortOrder),
    [triageQuestions],
  );

  const flagCount = useMemo(
    () => activeQuestions.filter(q => {
      const ans = triageAnswers[`q_${q.id}`];
      return q.flagIfAnswer && ans === q.flagIfAnswer;
    }).length,
    [activeQuestions, triageAnswers],
  );

  const set = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const handleScan = (data: DLData) => {
    setForm(f => ({
      ...f,
      firstName: data.firstName || f.firstName,
      lastName:  data.lastName  || f.lastName,
      dateOfBirth: data.dateOfBirth || f.dateOfBirth,
      gender: data.gender
        ? (data.gender === 'M' ? 'Male' : data.gender === 'F' ? 'Female' : f.gender)
        : f.gender,
    }));
    notifications.show({ title: 'ID Scanned', message: `${data.firstName} ${data.lastName}`, color: 'var(--color-success)' });
  };

  const shelterFamilies = useMemo(
    () => families.filter(f => String(f.incidentId) === String(activeIncident?.id ?? '')),
    [families, activeIncident],
  );

  const filteredFamilies = familySearch
    ? shelterFamilies.filter(f => f.familyName.toLowerCase().includes(familySearch.toLowerCase()))
    : shelterFamilies;

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      notifications.show({ title: 'Required', message: 'First and last name are required.', color: 'var(--color-error)' });
      return;
    }
    setSaving(true);
    try {
      const intakeTimestamp = new Date().toISOString();
      const intakeBy = (user as any)?.email ?? 'portal';

      const survivorData: Record<string, unknown> = {
        firstName: form.firstName.trim(),
        lastName:  form.lastName.trim(),
        dateOfBirth: form.dateOfBirth,
        gender: form.gender,
        phone: form.phone.trim(),
        emergencyContactName:  form.emergencyContactName.trim(),
        emergencyContactPhone: form.emergencyContactPhone.trim(),
        specialNeeds:  form.specialNeeds.trim(),
        dietaryNeeds:  form.dietaryNeeds.trim(),
        cotAssignment: form.cotAssignment.trim(),
        notes: form.notes.trim(),
        shelterId: String(shelter.id),
        incidentId: String(activeIncident?.id ?? ''),
        status: 'checked_in',
        intakeTimestamp,
        intakeBy,
      };

      if (familyMode === 'new' && newFamilyName.trim()) {
        const famId = await createFamily({
          familyName: newFamilyName.trim(),
          incidentId: String(activeIncident?.id ?? ''),
          memberCount: 1,
        } as any);
        if (famId) survivorData.familyId = String(famId);
      } else if (familyMode === 'existing' && selectedFamilyId) {
        survivorData.familyId = selectedFamilyId;
      }

      const survivorRid = await createSurvivor(survivorData);

      // Save triage answers — use q_${q.id} key (same as main intake)
      let flags = 0;
      for (const q of activeQuestions) {
        const qKey = `q_${q.id}`;
        const answer = triageAnswers[qKey];
        if (answer !== undefined && answer !== '') {
          const isFlagged = q.flagIfAnswer ? answer === q.flagIfAnswer : false;
          if (isFlagged) flags++;
          await createTriageAnswer({
            survivorId: String(survivorRid),
            questionId: qKey,
            answer,
            isFlagged,
            answeredAt: intakeTimestamp,
            answeredBy: intakeBy,
          });
        }
      }

      await loadShelters();
      setDoneFlags(flags);
      setDone(true);
      setTimeout(() => {
        setDone(false);
        setForm(empty());
        setTriageAnswers({});
        setFamilyMode('none');
        setNewFamilyName('');
        setSelectedFamilyId('');
        setFamilySearch('');
      }, 2000);
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to check in. Please try again.', color: 'var(--color-error)' });
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 16px' }}>
        <div style={{
          width: 64, height: 64, borderRadius: '50%', background: '#dcfce7',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <IconCheck size={32} color="#16a34a" />
        </div>
        <div style={{ fontSize: 20, fontWeight: 800, color: '#15803d', marginBottom: 8 }}>Checked In!</div>
        <div style={{ fontSize: 14, color: '#7B7D7F' }}>{form.firstName} {form.lastName} has been registered.</div>
        {doneFlags > 0 && (
          <div style={{ marginTop: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4, padding: '8px 16px', fontSize: 14, color: '#92400e', fontWeight: 600 }}>
            <IconFlag size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {doneFlags} triage response{doneFlags !== 1 ? 's' : ''} flagged for follow-up
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Pre-registered check-in */}
      <PreRegSection shelter={shelter} />

      {/* DL Scanner */}
      <DLScanner opened={scannerOpen} onClose={() => setScannerOpen(false)} onScan={handleScan} />

      {/* Scan ID button */}
      <button className="sp-scan-btn" onClick={() => setScannerOpen(true)}>
        <IconId size={20} />
        Scan Driver's License
        <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.75, marginLeft: 'auto' }}>auto-fill →</span>
      </button>

      {/* Personal Info */}
      <div className="sp-card">
        <div className="sp-card-title">Personal Information</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="sp-field">
            <label className="sp-label">First Name <span className="sp-req">*</span></label>
            <input className="sp-input" value={form.firstName} onChange={set('firstName')} placeholder="First" autoComplete="given-name" />
          </div>
          <div className="sp-field">
            <label className="sp-label">Last Name <span className="sp-req">*</span></label>
            <input className="sp-input" value={form.lastName} onChange={set('lastName')} placeholder="Last" autoComplete="family-name" />
          </div>
        </div>

        <div className="sp-field">
          <label className="sp-label">Date of Birth</label>
          <input className="sp-input" type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} />
        </div>

        <div className="sp-field">
          <label className="sp-label">Gender</label>
          <select className="sp-input sp-select" value={form.gender} onChange={set('gender')}>
            <option value="">— Select —</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Non-binary">Non-binary</option>
            <option value="Prefer not to say">Prefer not to say</option>
          </select>
        </div>

        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Phone</label>
          <input className="sp-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="(555) 555-5555" autoComplete="tel" />
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="sp-card">
        <div className="sp-card-title">Emergency Contact</div>
        <div className="sp-field">
          <label className="sp-label">Contact Name</label>
          <input className="sp-input" value={form.emergencyContactName} onChange={set('emergencyContactName')} placeholder="Full name" />
        </div>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Contact Phone</label>
          <input className="sp-input" type="tel" value={form.emergencyContactPhone} onChange={set('emergencyContactPhone')} placeholder="(555) 555-5555" />
        </div>
      </div>

      {/* Needs & Assignment */}
      <div className="sp-card">
        <div className="sp-card-title">Needs & Assignment</div>
        <div className="sp-field">
          <label className="sp-label">Special Needs</label>
          <input className="sp-input" value={form.specialNeeds} onChange={set('specialNeeds')} placeholder="Wheelchair, oxygen, interpreter…" />
        </div>
        <div className="sp-field">
          <label className="sp-label">Dietary Needs</label>
          <input className="sp-input" value={form.dietaryNeeds} onChange={set('dietaryNeeds')} placeholder="Diabetic, halal, allergy…" />
        </div>
        <div className="sp-field" style={{ marginBottom: 0 }}>
          <label className="sp-label">Cot Assignment</label>
          <input className="sp-input" value={form.cotAssignment} onChange={set('cotAssignment')} placeholder="Wing B, Cot 14" />
        </div>
      </div>

      {/* Triage / Conditioning Questions */}
      {activeQuestions.length > 0 && (
        <div className="sp-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div className="sp-card-title" style={{ marginBottom: 0 }}>Screening Questions</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: '#7B7D7F' }}>{activeQuestions.length} questions</span>
              {flagCount > 0 && (
                <span style={{ background: '#fef3c7', color: '#92400e', fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 99, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <IconFlag size={10} />{flagCount} flagged
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activeQuestions.map(q => (
              <MobileTriageField
                key={q.id}
                q={q}
                qKey={`q_${q.id}`}
                value={triageAnswers[`q_${q.id}`] ?? ''}
                onChange={v => setTriageAnswers(prev => ({ ...prev, [`q_${q.id}`]: v }))}
              />
            ))}
          </div>
        </div>
      )}

      {/* Family / Household */}
      <div className="sp-card">
        <div className="sp-card-title">Family / Household</div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {(['none', 'new', 'existing'] as const).map(m => (
            <button key={m} onClick={() => setFamilyMode(m)} style={{
              flex: 1, padding: '9px 4px', border: '1.5px solid', borderRadius: 4,
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              background: familyMode === m ? '#4B3FFF' : 'white',
              color:      familyMode === m ? 'white'    : '#475569',
              borderColor: familyMode === m ? '#4B3FFF'  : '#cbd5e1',
            }}>
              {m === 'none' ? 'Individual' : m === 'new' ? '+ New Family' : 'Join Existing'}
            </button>
          ))}
        </div>

        {familyMode === 'new' && (
          <div className="sp-field" style={{ marginBottom: 0 }}>
            <label className="sp-label">Family Name</label>
            <input className="sp-input" value={newFamilyName} onChange={e => setNewFamilyName(e.target.value)} placeholder="e.g. Smith Family" />
          </div>
        )}

        {familyMode === 'existing' && (
          <div>
            <div className="sp-field">
              <label className="sp-label">Search Families</label>
              <div style={{ position: 'relative' }}>
                <IconSearch size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#939598' }} />
                <input className="sp-input" style={{ paddingLeft: 34 }} value={familySearch}
                  onChange={e => setFamilySearch(e.target.value)} placeholder="Family name…" />
              </div>
            </div>
            <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 4 }}>
              {filteredFamilies.length === 0 ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#939598', fontSize: 14 }}>No families found</div>
              ) : filteredFamilies.map(f => (
                <div key={f.id} onClick={() => setSelectedFamilyId(String(f.id))} style={{
                  padding: '8px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  background: selectedFamilyId === String(f.id) ? '#eeeeff' : 'white',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{f.familyName}</span>
                  <span style={{ fontSize: 12, color: '#7B7D7F' }}>{f.memberCount} member{f.memberCount !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="sp-card">
        <div className="sp-card-title">Notes</div>
        <textarea className="sp-textarea" value={form.notes} onChange={set('notes')} placeholder="Any additional notes…" />
      </div>

      <button className="sp-btn sp-btn-primary" onClick={handleSubmit} disabled={saving} style={{ marginTop: 4 }}>
        {saving ? 'Checking in…' : <><IconPlus size={18} /> Check In</>}
      </button>
    </div>
  );
}
